import { Navigate, useNavigate } from "react-router-dom"

import { usePreInstallGithub } from "@/api/useIntegrations"
import { Breadcrumb, Form, Input, Link, Steps, Text, Title } from "@/atoms"
import { INTEGRATION_VENDORS } from "@/const/vendors-constants"
import { isUserAdmin } from "@/domain/user"
import { useLogger } from "@/hooks/useLogger"
import { useTranslation } from "@/hooks/useTranslation"
import { useUser } from "@/hooks/useUser"
import { HeaderPage } from "@/organisms/HeaderPage"
import { PageWithBreadcrumbTemplate } from "@/templates/PageWithBreadcrumbTemplate"
import { ConnectIntegrationFooter } from "../ConnectIntegrationPage/ConnectIntegrationFooter"

import { submitManifestToGitHub } from "./GitHubIntegrationPage.helper"

import connectStyles from "../ConnectIntegrationPage/ConnectIntegrationPage.module.scss"
import styles from "./GitHubIntegrationPage.module.scss"

import cn from "classnames"

export const GitHubIntegrationPage = () => {
  const { t } = useTranslation("translation", {
    keyPrefix: "integrations.connect.github.customApp"
  })
  const { t: tRoot } = useTranslation()
  const vendor = INTEGRATION_VENDORS.github
  const [form] = Form.useForm()
  const { logger, EVENTS } = useLogger()
  const navigate = useNavigate()
  const { user } = useUser()

  const orgValue = Form.useWatch("org", form)
  const hostValue = Form.useWatch("githubHost", form)

  const { refetch, isFetching } = usePreInstallGithub({
    org: orgValue || undefined,
    githubHost: hostValue || undefined
  })

  if (!isUserAdmin(user)) return <Navigate to="/integrations" />

  const status = "process" as const
  const requiredRule = { required: true, message: tRoot("error.requiredField") }
  const orgRules = [requiredRule]
  const hostRules = [
    requiredRule,
    { type: "url" as const, message: tRoot("error.invalidURL") }
  ]

  const onSubmit = async () => {
    try {
      await form.validateFields()
    } catch {
      return
    }
    logger.info(EVENTS.ANALYTIC_EVENTS.INTEGRATIONS.COMMAND_CONNECT, { vendor })
    const { data } = await refetch()
    const formAction = data?.data?.formAction
    const manifestJson = data?.data?.manifestJson
    if (formAction && manifestJson) {
      submitManifestToGitHub(formAction, manifestJson)
    }
  }

  return (
    <PageWithBreadcrumbTemplate
      full
      header={
        <HeaderPage
          title={
            <Breadcrumb
              items={[
                {
                  title: (
                    <Link to="/integrations">
                      {tRoot("navigation.integrations")}
                    </Link>
                  )
                },
                {
                  title: (
                    <Link to={`/integrations/${vendor}`}>{t("section")}</Link>
                  )
                },
                { title: t("breadcrumb") }
              ]}
            />
          }
        />
      }
      footer={
        <ConnectIntegrationFooter
          isLoading={isFetching}
          okText={t("connectButton")}
          onSubmit={onSubmit}
          onCancel={() => {
            form.resetFields()
            logger.info(EVENTS.ANALYTIC_EVENTS.INTEGRATIONS.COMMAND_CANCEL, {
              vendor
            })
            navigate(`/integrations/${vendor}`)
          }}
        />
      }
    >
      <Title className={connectStyles.title} level={4}>
        {t("title")}
      </Title>
      <Form layout="vertical" form={form}>
        <Steps
          className={cn(connectStyles.steps, styles.steps)}
          size="small"
          theme="dark"
          orientation="vertical"
          items={[
            {
              status,
              title: t("steps.organization.title"),
              content: (
                <Form.Item
                  name="org"
                  rules={orgRules}
                  extra={
                    <span className={styles.fieldDescription}>
                      {t("steps.organization.description")}
                    </span>
                  }
                >
                  <Input placeholder={t("steps.organization.placeholder")} />
                </Form.Item>
              )
            },
            {
              status,
              title: t("steps.host.title"),
              content: (
                <Form.Item name="githubHost" rules={hostRules}>
                  <Input placeholder={t("steps.host.placeholder")} />
                </Form.Item>
              )
            },
            {
              status,
              title: t("steps.complete.title"),
              content: (
                <Text>{t("steps.complete.description")}</Text>
              )
            }
          ]}
        />
      </Form>
    </PageWithBreadcrumbTemplate>
  )
}
